import axios from 'axios'
import { DELIVERY_METHOD } from 'domains/seller-inbound/constants'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'admins/consignment/inbound_requests'

const ConsignmentInboundRequestsApi = {

  /**
   * Create consignment inbound requests ednpoint.
   * Invoke `POST admins/consignment/inbound_requests`
   * @param {Number} seller_id - Seller Id
   * @param {Number} quantity - quantity of consignment inbound products
   * @param {Date} scheduled_at - scheduled date
   * @param {String} pickup_vehicle_type - vehicle that has been chosen to pickup inbound
   * @param {String} shipping_method - (previous : delivery_method) method for delivering inbound
   * @param {Object} pickup_address - user address used for inbound pickup
   * @param {Number} dropoff_location_city_id - Tinkerlust address dropoff location city id
   * @param {String} notes - additional notes
   */
  create: async ({
    seller_id,
    quantity,
    scheduled_at,
    shipping_method,
    pickup_address,
    dropoff_location_city_id,
    notes
  }) => {
    const path                    = API_PATH
    const isDropoffDeliveryMethod = shipping_method === DELIVERY_METHOD.DROPOFF
    const dropoffLocation         = isDropoffDeliveryMethod ? dropoff_location_city_id : ''
    const body                    = {
      consignment_inbound_request: {
        seller_id                : seller_id,
        quantity                 : quantity,
        scheduled_at             : scheduled_at,
        shipping_method          : shipping_method,
        pickup_address_id        : pickup_address?.id,
        notes                    : notes,
        dropoff_location_city_id : dropoffLocation
      }
    }
    const response                = axios.post(path, body)
    return response
  },

  /**
   * Get list of Consignment Inbound Request and its associations
   * Invoke `GET /admins/consignment/inbound_requests
   * @param {String}  code - represent code of ::Consignment::InboundRequest
   * @param {Number}  quantity - represent quantity of ::Consignment::InboundRequest
   * @param {String}  shipping_method - represent shipping_method of ::Consignment::InboundRequest
   * @param {String}  status - represent status of ::Consignment::InboundRequest
   * @param {Date}    received_at - represent received_at of ::Consignment::InboundRequest
   * @param {String}  seller_type - represent seller_type of ::Consignment::InboundRequest
   * @param {String}  seller_name - represent seller_name of ::Consignment::InboundRequest
   * @param {String}  seller_code - represent seller_code of ::Consignment::InboundRequest
   * @param {String}  seller_phone - represent seller_phone of ::Consignment::InboundRequest
   * @param {String}  seller_email - represent seller_email of ::Consignment::InboundRequest
   * @param {String}  pickup_vehicle_type - represent pickup_vehicle_type of ::Consignment::InboundRequest
   * @param {Date}    scheduled_at - represent scheduled_at of ::Consignment::InboundRequest
   * @param {String}  dropoff_location_city - represent dropoff_location of ::Consignment::InboundRequest
   * @param {String}  sort_by - represent sort_by of ::Consignment::InboundRequest
   * @param {String}  sort_dir - represent sort_dir of ::Consignment::InboundRequest
   * @param {Number}  page - represent page of ::Consignment::InboundRequest
   * @param {Number}  per_page - represent per_page of ::Consignment::InboundRequest
   */
  get: async ({
    //set default parameters
    code='',
    quantity='',
    shipping_method='',
    status='',
    received_at='',
    seller_type='',
    seller_name='',
    seller_code='',
    seller_phone='',
    seller_email='',
    pickup_vehicle_type='',
    scheduled_at='',
    dropoff_location_city='',
    sort_by='',
    sort_dir='',
    page                  = 1,
    per_page              = 10
  }) => {
    let params = {
      code                  : code,
      quantity              : quantity,
      shipping_method       : shipping_method,
      status                : status,
      received_at           : received_at,
      seller_type           : seller_type,
      seller_name           : seller_name,
      seller_code           : seller_code,
      seller_phone          : seller_phone,
      seller_email          : seller_email,
      pickup_vehicle_type   : pickup_vehicle_type,
      scheduled_at          : scheduled_at,
      dropoff_location_city : dropoff_location_city,
      sort_by               : sort_by,
      sort_dir              : sort_dir,
      page                  : page,
      per_page              : per_page
    }
    params     = ParamsUtil.removeEmptyAttributes(params)

    const path = API_PATH

    const response = await axios.get(path, { params })
    return response
  },

  /**
   * Get Consignment Inbound Request by id
   * Invoke `GET /admins/consignment/inbound_requests/:id
   * @param {Number} id - Id of Consignment Inbound Request
   */
  show: async ({ id }) => {
    const path = `${API_PATH}/${id}`

    const response = await axios.get(path)
    return response
  },

  /**
   * Receive Consignment Inbound Request by id
   * Invoke `PATCH /admins/consignment/inbound_requests/:id/receive
   * @param {Number} id - Id of Consignment Inbound Request
   * @param {Number} received_quantity - Quantity of received products
   * @param {Number} received_weight - Total Weight of received products
   * @param {String} pickup_tracking_number - shipment tracking number for pickup
   */
  receive: async ({
    id,
    received_quantity,
    received_weight,
    pickup_tracking_number
  }) => {
    const path = `${API_PATH}/${id}/receive`

    const payload = {
      consignment_inbound_request: {
        received_quantity      : received_quantity,
        received_weight        : received_weight,
        pickup_tracking_number : pickup_tracking_number
      }
    }

    const response = await axios.patch(path, payload)
    return response
  },

  bulkSchedule: async ({
    scheduled_at,
    courier,
    tracking_number = null
  }) => {
    const path = `${API_PATH}/bulk_schedule`

    const payload = {
      schedule_date   : scheduled_at,
      courier         : courier,
      tracking_number : tracking_number
    }

    const response = await axios.patch(path, payload)
    return response
  },

  /**
     * Create consignment inbound endpoint.
     * Invoke `POST` admins/consignment/inbounds
     * @param {Number} consignment_inbound_request_id - ID of ::Consignment::InboundRequest that will be generated into ::Seller::Inbound
     * @param {Object} values - formik values that contains classifications of item brands which have `tl_brand_classification` and `quantity`
     */
  updateInbounds: async ({
    consignment_inbound_request_id,
    values
  }) => {
    const path     = `${API_PATH}/${consignment_inbound_request_id}/inbounds`
    const params   = {
      consignment_inbound_request: {
        consignment_inbounds: values.seller_inbounds
      }
    }
    const response = axios.patch(path, params)
    return response
  },

  dropoffCities: async () => {
    const path = `${API_PATH}/dropoff_cities`

    const response = await axios.get(path)
    return response
  },

  schedulePickup: async ({
    id,
    courier,
    tracking_number = null
  }) => {
    const path =  `${API_PATH}/${id}/schedule`
    const body = {
      courier         : courier,
      tracking_number : tracking_number
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Confirm the Consignment Inbound Request
   * @param {number} id - Consignment Inbound Request ID
   * @param {string} pickup_vehicle_type - Vehicle Type to Pickup Inbound
   * @param {string} scheduled_at - scheduled_at value of Inbound Request
   */
  confirm: async ({ id, pickup_vehicle_type, scheduled_at }) => {
    const path     = `${API_PATH}/${id}/confirm`
    const payload  = {
      consignment_inbound_request: {
        pickup_vehicle_type : pickup_vehicle_type,
        scheduled_at        : scheduled_at
      }
    }
    const response = axios.patch(path, payload)
    return response
  },

  cancel: async (id) => {
    const path     = `${API_PATH}/${id}/cancel`
    const response = axios.patch(path)
    return response
  }
}

export default ConsignmentInboundRequestsApi